import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getNews, markNewsRead } from '../api';
import './PortalUI.css';

const TYPE_TONE = { 'Ny funktion': 'new', Forbedring: 'chg', Rettelse: 'ok' };
const dateText = (iso) => new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });

// "Nyheder" at the foot of a customer's portal's menu, next to Feedback: what NordComply has written about the latest updates. Nothing automatic.
export default function NewsMenu() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const popRef = useRef(null);
  const [anchor, setAnchor] = useState({ left: 0 });

  useEffect(() => {
    let cancelled = false;
    getNews().then((d) => { if (!cancelled) setData(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    // The panel opens beside the menu (like Feedback), so it is placed from the menu's right edge.
    const place = () => { const r = ref.current?.closest('.sidebar')?.getBoundingClientRect(); setAnchor({ left: (r ? r.right : 0) + 12 }); };
    place();
    window.addEventListener('resize', place);
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target) && !(popRef.current && popRef.current.contains(e.target))) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('resize', place); document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  function toggle() {
    const next = !open;
    // Placed before it first renders, so it does not jump in from the left edge.
    const r = ref.current?.closest('.sidebar')?.getBoundingClientRect();
    setAnchor({ left: (r ? r.right : 0) + 12 });
    setOpen(next);
    // Opened: they count as read from now on. What was new stays marked while the panel is open.
    if (next && data && data.unread > 0) markNewsRead();
  }

  const unread = open ? 0 : (data?.unread || 0);
  return (
    <div className="nw-menu" ref={ref}>
      <button type="button" className={`nw-btn${open ? ' is-open' : ''}`} onClick={toggle} aria-expanded={open}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h13v14H6a2 2 0 01-2-2zM17 9h3v8a2 2 0 01-2 2M8 9h5M8 13h5" /></svg>
        <span className="nw-btn__label">Nyheder</span>
        {unread > 0 && <em>{unread}</em>}
      </button>
      {open && createPortal(
        <div className="nw-pop nw-pop--fixed" style={{ left: anchor.left }} ref={popRef} role="dialog" aria-label="Nyheder">
          <div className="nw-ph"><b>Nyheder</b><small>Seneste opdateringer i NordComply</small></div>
          {!data && <p className="nw-empty">Henter…</p>}
          {data && data.items.length === 0 && <p className="nw-empty">Ingen nyheder endnu.</p>}
          {data && data.items.map((n) => (
            <div key={n.id} className={`nw-i${n.unread ? ' nw-i--new' : ''}`}>
              <div className="nw-m"><span className={`pu-chip pu-chip--${TYPE_TONE[n.type] || 'chg'}`}>{n.type}</span><small>{dateText(n.publishAt)}{n.author ? ` · ${n.author}` : ''}</small>{n.unread && <u>Ny</u>}</div>
              <b>{n.title}</b>
              <p>{n.body}</p>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
